<!-- IMPORTANT: This component is not intended to be used for stand-alone use.   -->
<!--    Please use ToastContainer   -->
<!--    NOTE: If 'primary' is passed as the display style, modify it to use the 'info' display style -->
<script setup lang="ts">
    import { Toast } from "bootstrap";
    import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from "vue"
    import Icon from "./../Icon.vue";
    import { IconTypes, HoverCursorTypes, DisplayStyle, ToastDisplayStyles } from '../../Enums/UiEnums'
    import { bootstrapSubtleBackgroundColor } from "../../lib/StyleHelpers";
    import { serverEnvironment } from "../../lib/Environment";
    import { EnvironmentType } from "../../Enums/ChoiceEnums";

    const props = withDefaults(defineProps<{
            id: string,
            title?: string;
            shortMsg?: string;
            hideDelayMilliseconds?: number;        
            displayStyle?: ToastDisplayStyles
            hideActionLink?: boolean,
            actionLinkText?: string,
        }>(), { 
            hideDelayMilliseconds: 5000,
            hideActionLink: false
    });
    
    const emit = defineEmits<{
        aboutToShow: [],
        aboutToHide: [],
        shown: [],
        closed: [],
        clickedAction: []
    }>();

    const theToast = ref<HTMLElement>();

    const safeShortMsg = computed(() => {
        if (!props.shortMsg)
            return null;

        if (props.shortMsg.length < 15)
            return props.shortMsg;

        return props.shortMsg.substring(0, 12) + "...";
    });
    
    onMounted(() => {
        const toastEle = theToast?.value as any;
        new Toast(toastEle).show();

        nextTick(() => {
            theToast.value?.addEventListener('show.bs.toast', onAboutToShowToast);
            theToast.value?.addEventListener('shown.bs.toast', onToastShown);
            theToast.value?.addEventListener('hide.bs.toast', onAboutToHideToast);
            theToast.value?.addEventListener('hidden.bs.toast', onToastClosed);
        });
    });

    onBeforeUnmount(() => {
        theToast.value?.removeEventListener('shown.bs.toast', onToastShown);
        theToast.value?.removeEventListener('hidden.bs.toast', onToastClosed);
    });

    const backgroundClass = computed(() => {
        const backgroundStyle = (props.displayStyle === DisplayStyle.Info ? DisplayStyle.Primary : props.displayStyle) ?? DisplayStyle.Primary;
        return bootstrapSubtleBackgroundColor(backgroundStyle);
    });

    function onAboutToShowToast()
    {
        emit("aboutToShow");
    }

    function onAboutToHideToast()
    {
        emit("aboutToHide");
    }

    function onToastShown()
    {
        emit("shown");
    }

    function onToastClosed()
    {
        emit("closed");
    }

    const hasActionEvent = computed<boolean>(() => {
        const compProps = getCurrentInstance()?.vnode.props;
        return compProps?.onClickedAction && !props.hideActionLink;
    });

    const iconType = computed<IconTypes>(() =>{

        const iconsByDisplayStyle: Record<ToastDisplayStyles, IconTypes> = {
            [DisplayStyle.Info]: IconTypes.Information,
            [DisplayStyle.Success]: IconTypes.CheckMark,
            [DisplayStyle.Warning]: IconTypes.Warning,
            [DisplayStyle.Danger]: IconTypes.Error
        };
        return iconsByDisplayStyle[props.displayStyle ?? DisplayStyle.Info] ?? IconTypes.Information;
    });

    const iconTitle = computed<string|undefined>(() => {
        const iconsByDisplayStyle: Record<ToastDisplayStyles, string> = {
            [DisplayStyle.Info]: "Information",
            [DisplayStyle.Success]: "Success",
            [DisplayStyle.Warning]: "Warning",
            [DisplayStyle.Danger]: "Danger"
        };        
        return iconsByDisplayStyle[props.displayStyle ?? DisplayStyle.Info];
    });

    const computedTitle = computed(() => {
        if (props.title)
            return props.title;

        switch(<DisplayStyle>props.displayStyle)
        {
            case DisplayStyle.Primary:
            case DisplayStyle.Info: 
            return "Information";

            case DisplayStyle.Success: return "Success";
            case DisplayStyle.Danger: return "Danger";
            case DisplayStyle.Warning: return "Warning";
        }

        const msg = `No handler for toast display style: {props.displayStyle}`;
        console.log(msg); 
        switch(serverEnvironment())
        {
            case EnvironmentType.Dev:
            case EnvironmentType.QA:
            alert(msg);
        }
        return "Status";
    });

    const iconDisplayStyle = computed(() => {
        switch (<DisplayStyle>props.displayStyle)
        {
            case DisplayStyle.Info:
            case DisplayStyle.Primary:
                return DisplayStyle.Primary;

            default:
                return props.displayStyle;
        }
    });


</script>

<template>
    <div ref="theToast" :id="props.id" class="toast" role="alert" aria-live="assertive" aria-atomic="true" :data-bs-delay="hideDelayMilliseconds">
        <div class="toast-header" :class="backgroundClass" style="border-bottom: 0px;">
            <!-- Allow a small image? -->
            <div class="me-1" style="width:1rem">
                <Icon :icon="iconType" :title="iconTitle" :displayStyle="iconDisplayStyle" />
            </div>

            <strong class="me-auto">
                {{computedTitle}}
            </strong>

            <div class="float-end">
                <small class="text-muted" v-if="safeShortMsg">
                    {{safeShortMsg}}
                </small>
                <Icon 
                    :icon="IconTypes.CircleXMark" 
                    :hoverCursor="HoverCursorTypes.Pointer" 
                    :displayStyle="iconDisplayStyle"
                    title="Close message" 
                    data-bs-dismiss="toast" 
                    aria-label="Close" />
            </div>
        </div>
        <div class="toast-body" :class="backgroundClass" style="padding-left:2rem; padding-top:0px">
            <slot></slot>

            <slot name="action">
                <div v-if="hasActionEvent">
                    <a @click="emit('clickedAction')" class="link-primary" style="cursor:pointer">
                        {{ props.actionLinkText ?? "Action" }}
                    </a>
                </div>
            </slot>
        </div>
    </div>   
</template>